/* eslint-disable no-underscore-dangle */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, memo, useCallback, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import type { MDXRemoteSerializeResult } from 'next-mdx-remote';
import parseLocale from '@utils/parse-locale';
import waitForGlobal from '@utils/wait-for-global';
import SectionWrapper from '@components/SectionWrapper';
import ButtonLink from '@components/ButtonLink';
import Button from '@components/Button';
import { ZENDESK_TICKET_URL } from '@constants';
import DetailsColumns from './DetailsColumns';
import * as styles from './styles';

type AddressColumnProps = {
  _modelApiKey?: string;
  addressField?: string;
  directorField?: string;
  phoneNumberField?: string;
  hoursField?: string;
  addressFieldMd: MDXRemoteSerializeResult;
  directorFieldMd?: MDXRemoteSerializeResult;
  phoneNumberFieldMd?: MDXRemoteSerializeResult;
  hoursFieldMd?: MDXRemoteSerializeResult;
  holidayScheduleMd?: MDXRemoteSerializeResult;
  notification?: string;
  notificationMd?: MDXRemoteSerializeResult;
};

interface Props {
  markdownFields: AddressColumnProps[];
  heading:
    | {
        eyebrow?: string;
        heading?: string;
        subheading?: string;
      }
    | string;
}

const DetailsSection: FC<Props> = ({ markdownFields, heading }) => {
  const { locale, defaultLocale } = useRouter();
  const parsedLocale = parseLocale(defaultLocale, locale);
  const langForZendesk = parsedLocale[0] === 'en' ? 'en-us' : parsedLocale[0];

  const [openChat, setOpenChat] = useState(false);

  useEffect(() => {
    if (!openChat && typeof window !== 'undefined') {
      waitForGlobal('zE', () => {
        setOpenChat(true);
      });
    }
  }, [openChat]);

  const onClick = useCallback(() => {
    if (openChat) {
      window.zE('messenger', 'open');
    }
  }, [openChat]);

  return (
    <SectionWrapper data-test-id="details-section-block" noSpacing>
      {typeof heading === 'string' && (
        <h2
          className={styles.detailsHeading}
          data-test-id="details-section-heading"
        >
          {heading}
        </h2>
      )}
      <div className="mb-8">
        {openChat && (
          <Button
            buttonStyle="primary"
            type="button"
            onClick={onClick}
            customMargin
            className={styles.contactButton}
          >
            Chat with Us
          </Button>
        )}
        <ButtonLink
          asButton
          buttonStyle="secondary"
          href={ZENDESK_TICKET_URL.replace('en-us', langForZendesk)}
          target="_blank"
          className={styles.contactButton}
          customMargin
        >
          Email Us
        </ButtonLink>
      </div>
      <DetailsColumns addressColumnData={markdownFields} />
    </SectionWrapper>
  );
};

export default memo(DetailsSection);
